// src/domains/shell.ts —— H5 外壳（原生）：boot / 会话 / TG 适配 / TabBar / 弹层合成 / U-21 动态码 / S-23 会话页
import { ApiError } from '../../../shared/src/api/client';
import type { Profile, PublicConfig, TokenResp } from '../../../shared/src/api/types';
import { takeRef } from '../../../shared/src/ref';
import { writeLocal } from '../../../shared/src/storage';
import { getTg } from '../../../shared/src/tg';
import type { H5Gp, H5Session, H5Totp } from '../gp';
import { I18N_ERRORS } from '../i18n/errors';
import { I18N_SHELL } from '../i18n/shell';
import type { H5State, Screen } from '../state';
import { gateMissing } from './gate';
import { KEYS, MODAL_KEYS } from './shell.keys';
import { botIdField, str, type ClickEvent, type H5Ctx, type H5Domain, type InputEvent } from './types';

const ROOT_SCREENS: readonly Screen[] = ['home', 'wallet', 'me'];
// 无会话 / 终态页：不显示 TG BackButton，BackButton 回调不导航（S-23）
const TERMINAL_SCREENS: readonly Screen[] = ['login', 'closed', 'sessionExpired'];
// 原型 L635 tabs + L638 的不显示 TabBar 屏；myCards 按 U-02a；会话页无 TabBar
const NO_TABS: readonly Screen[] = ['login', 'products', 'openForm', 'cardDetail', 'deposit', 'referral', 'support', 'kyc', 'withdrawUsdt', 'messages', 'security', 'fees', 'myCards', 'closed', 'sessionExpired'];
// U-20 TG BackButton up 映射
const UP: Readonly<Partial<Record<Screen, Screen>>> = {
  products: 'home', openForm: 'products', cardDetail: 'home', myCards: 'home', deposit: 'wallet', withdrawUsdt: 'wallet',
  kyc: 'me', messages: 'me', security: 'me', referral: 'me', support: 'me', fees: 'me',
};
const TOTP_LEN = 6;
// U-22 E-04：次级列表首屏骨架 3 行（各域列表片共用）
const SKEL_ROWS: ReadonlyArray<{ i: number }> = [{ i: 0 }, { i: 1 }, { i: 2 }];
let unForeground: (() => void) | null = null;

// 原型 L609 tabStyle 逐字
function tabStyle(active: boolean): string {
  return `border:0;background:transparent;display:flex;flex-direction:column;align-items:center;gap:3px;cursor:pointer;color:${active ? '#0f1a17' : '#9aa5a1'};padding:4px 0`;
}

let gpOf: (() => H5Gp) | null = null;
let totpResolve: ((code: string | null) => void) | null = null;
let backHandler: (() => void) | null = null;

function gp(): H5Gp {
  if (!gpOf) throw new Error('shell 未挂载');
  return gpOf();
}

function setSh(h: { state: H5State; setState(u: Partial<H5State>): void }, patch: Partial<H5State['sh']>): void {
  h.setState({ sh: { ...h.state.sh, ...patch } });
}

export function sessionImpl(): H5Session {
  return {
    async established(token, opts) {
      const g = gp();
      const h = g.host();
      g.tokens.set(token, opts.persist);
      let profile: Profile;
      try {
        profile = await g.api.call<Profile>('GET /api/user/profile');
      } catch (e) {
        if (opts.rollbackOnFail) g.tokens.clear();
        throw e;
      }
      h.setState({ lang: profile.lang === 'en' ? 'en' : 'zh', screen: 'home', booting: true, modal: null, sh: { ...h.state.sh, profile, termsBanner: !!opts.termsBanner } });
      g.events.emit('session:ready', { reason: opts.toast ? 'login' : 'boot' });
      if (opts.toast) g.ui.toast(opts.toast, 'success');
    },
    async reloadProfile() {
      const g = gp();
      const profile = await g.api.call<Profile>('GET /api/user/profile');
      setSh(g.host(), { profile });
      g.events.emit('profile', {});
    },
    setLang(lang) {
      const g = gp();
      const h = g.host();
      h.setState({ lang, modal: null });
      if (h.state.screen === 'login' || !h.state.sh.profile) {
        writeLocal('gpu_lang', lang);
        return;
      }
      // U-19：乐观更新，失败重试 1 次仍失败 toast，界面保持新语言
      const put = () => g.api.call('PUT /api/user/profile', { body: { lang } });
      put()
        .catch(() => put())
        .catch((e: unknown) => g.ui.toast(g.ui.err(e), 'error'));
    },
    async logout() {
      const g = gp();
      try {
        await g.api.call('POST /api/user/logout');
      } catch (e) {
        console.warn('[gp:h5] logout 请求失败，本地照常退出', e);
      }
      g.tokens.clear();
      const h = g.host();
      h.setState({ screen: 'login', modal: null, booting: false, sh: { ...h.state.sh, profile: null, termsBanner: false, totp: null } });
      totpResolve?.(null);
      g.events.emit('session:lost', { reason: 'logout' });
    },
  };
}

export function totpImpl(): H5Totp {
  return {
    async run(action, opts) {
      const g = gp();
      const h = g.host();
      if (!h.state.sh.profile?.totp_enabled) return action(undefined);
      const d = g.ui.dict();
      setSh(h, { totp: { code: '', err: '', waitNext: !!opts?.waitNext, locked: false } });
      g.ui.openModal({ type: 'totp', title: str(d, 'totpTitle') });
      for (;;) {
        const code = await new Promise<string | null>((res) => {
          totpResolve = res;
        });
        totpResolve = null;
        if (code === null) {
          setSh(g.host(), { totp: null });
          return null;
        }
        try {
          // busy 经 ui.nest 置 totp 并在结束后恢复调用方外层键（U-20 互斥不被动态码步骤提前解除）
          const out = await g.ui.nest('totp', () => action(code));
          setSh(g.host(), { totp: null });
          g.ui.closeModal();
          return out;
        } catch (e) {
          const cur = g.host().state.sh.totp;
          // 1006 码错可重输；1002 / 429 锁定期间不自动提交（U-21）
          if (e instanceof ApiError && (e.code === 1006 || e.code === 1002 || e.status === 429)) {
            setSh(g.host(), { totp: { code: '', err: g.ui.err(e), waitNext: cur?.waitNext ?? false, locked: e.code !== 1006 } });
            continue;
          }
          setSh(g.host(), { totp: null });
          g.ui.closeModal();
          throw e;
        }
      }
    },
  };
}

async function boot(ctx: H5Ctx): Promise<void> {
  const g = ctx.gp;
  const h = ctx.host;
  const url = new URL(window.location.href);
  const botId = url.searchParams.get('bot') ?? '';
  const tg = getTg();
  // 归属修复（2026-10-21）：URL ?ref= 优先、其次 Mini App start_param；新码覆盖旧码并留存 30 天（localStorage，shared/ref），
  // 均无时读回未过期的留存码——刷新丢 query、新开标签、隔天直开 H5 仍能带码注册。只影响注册预填，已登录 / 已注册用户不受影响。
  const ref = takeRef(url.searchParams.get('ref') || tg?.initDataUnsafe?.start_param || '', Date.now()) ?? '';
  const u = tg?.initDataUnsafe?.user;
  setSh(h, { botId, ref, miniApp: !!tg?.initData, inTelegram: !!tg, tgUser: u ? (u.username ? '@' + u.username : u.first_name ?? '') : '', tgId: u?.id ? String(u.id) : '' });
  if (tg) {
    try {
      tg.ready();
      tg.expand?.();
      tg.setHeaderColor?.('#0f1a17');
      tg.setBackgroundColor?.('#f5f7f6');
      backHandler = () => tgBack(ctx);
      tg.BackButton?.onClick(backHandler);
    } catch (e) {
      console.warn('[gp:h5] TG 初始化失败', e);
    }
  }
  let config: PublicConfig | null = null;
  try {
    config = await g.api.call<PublicConfig>('GET /api/public/config', { query: { bot_id: botId || undefined } });
    setSh(h, { config });
  } catch (e) {
    g.ui.toast(g.ui.err(e), 'error');
  }
  try {
    // S-23 / P-09：关闭页仅约束 Mini App 入口（按 Bot 的 mini_app_on）；浏览器直开 H5 不受影响（未带 bot_id 时下发的是平台 Bot）
    if (tg?.initData && config?.bot && config.bot.mini_app_on === false) {
      h.setState({ screen: 'closed' });
      return;
    }
    if (tg?.initData) {
      try {
        const r = await g.api.call<TokenResp & { created?: boolean }>('POST /api/public/login/telegram', { body: { init_data: tg.initData, ...botIdField(botId) } });
        // TD-811：Mini App 受限会话 token 仅内存
        await g.session.established(r.token, { persist: false, termsBanner: true });
      } catch (e) {
        h.setState({ screen: 'login' });
        g.events.emit('session:lost', { reason: e instanceof ApiError && e.code === 1503 ? 'tg_register_closed' : 'tg_auth_failed' });
      }
      return;
    }
    const hasToken = !!g.tokens.get() || (await g.api.tryRefresh());
    if (hasToken) {
      try {
        const token = g.tokens.get();
        if (token) {
          await g.session.established(token, { persist: true });
          return;
        }
      } catch (e) {
        console.warn('[gp:h5] 会话恢复失败', e);
      }
    }
    h.setState({ screen: 'login' });
    void checkRef(ctx, botId, ref);
  } finally {
    h.setState({ appBoot: false });
    syncBackButton(h.state.screen);
  }
}

// 归属修复（2026-10-21）U-01：仅「浏览器形态落登录页且带推广码」时单独校验码有效性（config?ref= → ref_valid），结果只写 sh.refValid、
// 不替换 sh.config——已登录用户与 Mini App 形态完全不受影响（bot 段 / 关闭页判定仍按入口 Bot）。失败 = 未确认有效，邀请码保持可编辑。
async function checkRef(ctx: H5Ctx, botId: string, ref: string): Promise<void> {
  if (!ref) return;
  try {
    const c = await ctx.gp.api.call<PublicConfig>('GET /api/public/config', { query: { bot_id: botId || undefined, ref } });
    setSh(ctx.host, { refValid: c.ref_valid === true });
  } catch (e) {
    console.warn('[gp:h5] 推广码校验失败，邀请码保持可编辑', e);
  }
}

function tgBack(ctx: H5Ctx): void {
  const h = ctx.host;
  if (h.state.modal) {
    closeModal(ctx);
    return;
  }
  if (TERMINAL_SCREENS.includes(h.state.screen)) return;
  ctx.gp.ui.go(UP[h.state.screen] ?? 'home');
}

function syncBackButton(screen: Screen): void {
  const tg = getTg();
  if (!tg?.BackButton) return;
  try {
    if (ROOT_SCREENS.includes(screen) || TERMINAL_SCREENS.includes(screen)) tg.BackButton.hide();
    else tg.BackButton.show();
  } catch (e) {
    console.warn('[gp:h5] BackButton', e);
  }
}

function closeModal(ctx: H5Ctx): void {
  if (ctx.host.state.modal?.type === 'totp' && totpResolve) totpResolve(null);
  ctx.gp.ui.closeModal();
}

export function createShell(features: () => readonly H5Domain[]): H5Domain {
  return {
    name: 'shell',
    keys: KEYS,
    modalKeys: MODAL_KEYS,
    migrated: true,
    i18n: { zh: { ...I18N_ERRORS.zh, ...I18N_SHELL.zh }, en: { ...I18N_ERRORS.en, ...I18N_SHELL.en } },
    rewrites: [],
    mount(ctx) {
      gpOf = () => ctx.gp;
      ctx.events.on('home:ready', () => ctx.host.setState({ booting: false }));
      ctx.events.on('session:lost', ({ reason }) => {
        const h = ctx.host;
        if (reason === 'miniapp_expired') h.setState({ screen: 'sessionExpired', modal: null });
        else if (reason === 'expired') {
          h.setState({ screen: 'login', modal: null, booting: false, sh: { ...h.state.sh, profile: null, totp: null } });
          totpResolve?.(null); // 释放挂起的动态码 Promise（调用方得到 null，与用户取消同语义）
        }
      });
      // U-22 E-05：回前台立即刷新 profile（unread_count / fee_owed）并通知各域重拉当前页查询；无会话（登录页 / 终态页）不触发
      unForeground?.();
      unForeground = ctx.gp.foreground.watch(() => {
        const h = ctx.host;
        if (!h.state.sh.profile || TERMINAL_SCREENS.includes(h.state.screen)) return;
        ctx.gp.session.reloadProfile().catch((e: unknown) => console.warn('[gp:h5] 回前台刷新 profile 失败', e));
        ctx.gp.foreground.fire();
      });
      void boot(ctx).catch((e: unknown) => {
        console.error('[gp:h5] boot 失败', e);
        ctx.host.setState({ appBoot: false, screen: 'login' });
      });
    },
    update(ctx, prev) {
      if (prev.screen !== ctx.s.screen) {
        ctx.host.setState({ sh: { ...ctx.s.sh, navFrom: prev.screen } });
        ctx.events.emit('screen', { from: prev.screen, to: ctx.s.screen });
        syncBackButton(ctx.s.screen);
      }
    },
    unmount() {
      const tg = getTg();
      if (backHandler) tg?.BackButton?.offClick(backHandler);
      backHandler = null;
      if (totpResolve) totpResolve(null);
      unForeground?.();
      unForeground = null;
      gpOf = null;
    },
    vals(ctx) {
      const { s, t } = ctx;
      const m = s.modal ?? { type: '' };
      const totp = s.sh.totp;
      const parts = features().map((d) => d.modal?.(ctx) ?? {});
      const title = m.type === 'totp' ? str(t, 'totpTitle') : m.title ?? '';
      return {
        t,
        appBoot: s.appBoot,
        isApp: !TERMINAL_SCREENS.includes(s.screen),
        showTabs: !NO_TABS.includes(s.screen),
        stop: (e: ClickEvent) => e.stopPropagation(),
        tabHomeStyle: tabStyle(s.screen === 'home'),
        tabWalletStyle: tabStyle(s.screen === 'wallet'),
        tabMeStyle: tabStyle(s.screen === 'me'),
        goHome: () => ctx.gp.ui.go('home'),
        goWallet: () => ctx.gp.ui.go('wallet'),
        goMe: () => ctx.gp.ui.go('me'),
        // U-24：未开通邮箱登录（email_login_missing 非 null）点充值 / 提现改弹引导，不进入下单页
        goDeposit: () => {
          if (!ctx.gp.gate.guard()) ctx.gp.ui.go('deposit');
        },
        goWithdrawUsdt: () => {
          if (!ctx.gp.gate.guard()) ctx.gp.ui.go('withdrawUsdt');
        },
        // U-24 常驻提示条（首页 0083 / 我的 0255 顶部）
        showGateBar: gateMissing(s.sh.profile) !== null,
        gateBarText: str(t, 'gateBar'),
        gateBarBtnText: str(t, 'gateBarBtn'),
        openGate: () => {
          const m = ctx.gp.gate.missing();
          if (m) ctx.gp.gate.open(m);
        },
        setZh: () => ctx.gp.session.setLang('zh'),
        setEn: () => ctx.gp.session.setLang('en'),
        tgUserText: s.sh.tgUser,
        tgIdText: s.sh.tgId,
        toast: s.toast,
        modalOpen: !!s.modal,
        closeModal: () => closeModal(ctx),
        modal: Object.assign(
          {
            title,
            isInfo: m.type === 'info' || m.type === 'ticket',
            isTicket: m.type === 'ticket',
            rows: m.rows ? m.rows.map(([k, v]) => ({ k, v })) : [],
            isTotp: m.type === 'totp',
            note: typeof m.note === 'string' ? m.note : '', // U-17 / U-18 rows 下方注（S-45 feeNoRefundTip）
          },
          ...parts,
        ),
        isSessionPage: s.screen === 'closed' || s.screen === 'sessionExpired',
        sessionPageText: s.screen === 'closed' ? str(t, 'miniClosed') : str(t, 'sessionExpired'),
        closeApp: () => getTg()?.close?.(),
        totpSubText: str(t, 'totpSub') + (totp?.waitNext ? ' ' + str(t, 'totpWaitNext') : ''),
        totpCode: totp?.code ?? '',
        onTotpCode: (e: InputEvent) => {
          const code = e.target.value.replace(/\D/g, '').slice(0, TOTP_LEN);
          const cur = ctx.host.state.sh.totp;
          if (!cur) return;
          setSh(ctx.host, { totp: { ...cur, code, err: '' } });
          if (code.length === TOTP_LEN && !cur.locked && totpResolve) totpResolve(code);
        },
        totpErr: totp?.err ?? '',
        cancelTotp: () => closeModal(ctx),
        confirmTotp: () => {
          const cur = ctx.host.state.sh.totp;
          if (cur && cur.code.length === TOTP_LEN && totpResolve) totpResolve(cur.code);
          else if (cur) setSh(ctx.host, { totp: { ...cur, err: str(t, 'err2fa'), code: '' } });
        },
        busyTotp: s.busy === 'totp',
        skelRows: SKEL_ROWS,
        retryText: str(t, 'retry'),
      };
    },
  };
}
